import type { ToneNotation } from "@kensio/pinyinjs";

import type { SegmenterElements } from "./segmenter-elements.js";
import { showOrder } from "./segmenter-ordering.js";
import { wordRows } from "./segmenter-rows.js";
import { summaryText } from "./segmenter-summary.js";
import {
  orderedWords,
  type SegmentedWord,
  totalWords,
  type WordOrder,
} from "./segmented-words.js";

/**
 * Putting the word list on the page, and saying what order it is in.
 *
 * Separate from `segmenter-session.ts`, which decides when to read the text
 * and what to read it with. The cap lives here too, because holding the box to
 * it and saying how much of it is left are the same act: a view that truncated
 * the text and left the counter alone would show a reader a number that is not
 * about the text in front of them.
 */

/**
 * How much text the tool takes, in characters.
 *
 * Measured on a laptop over Tatoeba sentences, the `standard` tier segments
 * 1,000 characters in 4.3 ms, 5,000 in 18 ms and 30,000 in 101 ms, and the
 * cost is linear in the length.
 *
 * **Segmenting and drawing 5,000 characters of running text logs no long task
 * at all**, measured in Chromium with a `PerformanceObserver` over the site's
 * own 例句 as the sample. A long task is 50 ms, so the whole of it, the
 * segmentation and 751 rows of table, stays under that.
 *
 * The worst input that fits the cap is 5,000 consecutive rare characters,
 * where every character is a distinct word and the decoder has the least to go
 * on. That is 3,639 rows and one 134 ms task. Noticeable, and it is a text
 * nobody types.
 *
 * 5,000 characters is a long news article or a short chapter, which is the
 * size of thing this tool is for. 1,000 would have been a page and a half.
 */
export const inputCap = 5000;

/** What the counter says under the box. */
function counterText(length: number): string {
  return `${length.toLocaleString()} of ${inputCap.toLocaleString()} characters`;
}

/** Everything the segmenter writes to the page. */
export class SegmenterView {
  readonly #elements: SegmenterElements;

  constructor(elements: SegmenterElements) {
    this.#elements = elements;
  }

  /**
   * Hold the text to the cap, and say how much of it is used.
   *
   * A paste over the cap is truncated rather than refused, because somebody
   * who pasted a whole book still wants an answer about the start of it, and a
   * box that silently rejected the paste would look broken.
   */
  holdToCap(): void {
    const { input, counter } = this.#elements;

    if (input.value.length > inputCap) {
      input.value = input.value.slice(0, inputCap);
      counter.dataset["cbCapped"] = "true";
    } else {
      delete counter.dataset["cbCapped"];
    }

    counter.textContent = counterText(input.value.length);
  }

  /**
   * Put a list of words on the page, in the order asked for.
   *
   * The ordering happens here rather than in the tool because it is the only
   * thing reordering changes: the same words come back in a different sequence
   * and nothing has to be read again.
   */
  show(
    words: readonly SegmentedWord[],
    order: WordOrder,
    notation: ToneNotation,
  ): void {
    const { list, summary, empty, input } = this.#elements;
    const shown = orderedWords(words, order);

    list.replaceChildren(...wordRows(shown, notation));
    summary.textContent = summaryText(shown, totalWords(shown));

    empty.hidden = !(input.value.trim() !== "" && shown.length === 0);

    showOrder(this.#elements.ordering, order);
  }
}
